import {unescape} from 'underscore'
import {RENDER_FORMATS, STATUS_TYPES} from './constants'
import {isDefined} from './utils'
import LinkNavigator from './linkNavigator'

window.Render = window.Render || {}
const ns = window.Render

const isClientUrl = url => /^client:\/\//.exec(url)

class InvalidFormatError extends Error {
  constructor(message) {
    super(message)
    this.name = 'InvalidFormat'
  }
}

/**
 * Responsible for handling communication between the render DOM and it's parent iframe
 *
 * @param format Valid renderable file format @see RENDER_FORMATS
 * @param opts.allowLinks? Should this class allow redirects from link within the render area?
 * @param opts.initial? The initial status message to be communicated
 */
class Status {
  constructor(format, opts = {}) {
    this.sendInitial = this.sendInitial.bind(this)
    this.onReady = this.onReady.bind(this)
    this.onAck = this.onAck.bind(this)
    this.requireAck = this.requireAck.bind(this)
    this.submitTiming = this.submitTiming.bind(this)
    this.submitGiveup = this.submitGiveup.bind(this)
    this.handleData = this.handleData.bind(this)
    this.handleCmd = this.handleCmd.bind(this)
    this.handleMessage = this.handleMessage.bind(this)
    this.haveParent = this.haveParent.bind(this)
    this.githubHostname = $('body').data('github-hostname')
    this.githubEnv = $('body').data('deploy-env')
    this.format = format
    this.initialStatusTimeout = null

    if (opts.allowLinks) {
      const linkNavigator = new LinkNavigator({hasParent: this.haveParent()})
      $(document).on('click', 'a', linkNavigator.handleLink)
    }

    /*
      If this error is hit, it's likely that a new format was added to the backend server and not updated in the constants.js RENDER_FORMATS map.
    */
    if (!this.isValidFormat(this.format)) {
      throw new InvalidFormatError('Unsupported `format` declared when Status was constructed.')
    }
    if (window.location.hash.length) {
      this.identity = window.location.hash.substring(1)
    }
    this.sentData = {
      have: [], // Data sent to us waiting for a consumer
      waiting: [] // Consumers that want data, but don't have it yet
    }
    this.messages = [
      {status: STATUS_TYPES.constructor, payload: undefined, when: Date.now(), sent: true} // Debugging ctor call timing
    ]
    this.acked = false
    $(window).on('message', this.handleMessage)

    const initial = opts.initial || STATUS_TYPES.hello

    if (this.haveParent()) {
      this.sendInitial(initial, 10, 1000)
    }
  }

  isValidFormat(format) {
    return Object.values(RENDER_FORMATS).includes(format)
  }

  /**
   * This function is responsible for communicating the initial status of the renderer back to the parent frame.
   * Necessary as the render JS functionality is embedded in an iframe. This function will attempt to
   * contact its parent frame until it receives a response or exhausts the pool of attempts.
   * @param {STATUS_TYPES} initial The initial status message we want to send to the parent frame
   * @param {number} remaining How many attempts will we make to contact the parent frame before giving up?
   * @param {number} interval Frequency with which we try and contact the parent frame
   * @returns
   */
  sendInitial(initial, remaining = 10, interval = 1000) {
    if (this.acked || remaining < 1) {
      clearTimeout(this.initialStatusTimeout)
      return
    }

    this.initialStatusTimeout = setTimeout(() => this.sendInitial(initial, remaining - 1, interval), interval)
    window.debug(
      `Sending hello. Will try again ${remaining} more times in ${interval / 1000} second intervals until ack'd`
    )
    return this.set(initial)
  }

  load(url, options = {}) {
    if (options.setStatus == null) {
      options.setStatus = true
    }
    if (options.attempts == null) {
      options.attempts = this.clientTimeoutAttempts()
    }
    if (options.before == null) {
      options.before = function (xhr, settings) {}
    }
    if (options.success == null) {
      options.success = (data, xhr) => {
        if (options.setStatus) {
          return this.set(STATUS_TYPES.loaded)
        }
      }
    }
    if (options.error == null) {
      options.error = (error, xhr) => {
        if (options.setStatus) {
          return this.set(STATUS_TYPES.error)
        }
      }
    }
    if (options.timeout == null) {
      options.timeout = 30000
    }

    if (options.setStatus) {
      this.set(STATUS_TYPES.loading)
    }

    if (isClientUrl(url)) {
      return this.loadClient(url, options)
    } else {
      return this.loadHttp(url, options)
    }
  }

  //# Raw status access
  // Handles whether or not to post a message to the parent window
  // TODO: Rename? The name set is incredibly generic and doesn't really encapsulate
  // what this method does. It also does two things, adding a message to the message list
  // and posting that message?
  set(status, payload = {}) {
    const sent = this.alreadySentStatus(status)

    if (isDefined(sent) && ![STATUS_TYPES.hello, STATUS_TYPES.resize].includes(status)) {
      const ago = Math.abs(Date.now() - sent.when)
      return window.debug(`Already set status '${status}' ${ago}ms ago`)
    } else {
      const message = {status, payload, when: Date.now(), sent: false}
      // Don't push duplicate hello messages
      // However, it seems that we do still send duplicate `hello` messages
      if (!sent || status !== STATUS_TYPES.hello) {
        this.messages.push(message)
      }

      if (status === STATUS_TYPES.ready) {
        this.onReady()
      }

      if (!this.requireAck(status)) {
        message.sent = true
        return this.post({
          type: 'render',
          body: status,
          payload
        })
      }
    }
  }

  alreadySentStatus(status) {
    const found = this.messages.filter(e => e.status === status)
    return found[0]
  }

  //# Private
  prepareData(data, options) {
    let result = data

    if (options.json && typeof data === 'string') {
      try {
        result = JSON.parse(data)
      } catch (error) {
        // Apparently Mobile Safari requires this, though it is unclear why.
        // Google searches didn't yield fruit
        window.debug(`Error while trying to parse initial JSON: ${error}`)
        window.debug('Attempting to parse htmlDecoded JSON')
        try {
          result = JSON.parse(unescape(data))
        } catch (e) {
          this.set(STATUS_TYPES.error)
        }
      }
    }

    return result
  }

  loadClient(url, options) {
    if (!isClientUrl(url)) {
      throw new Error(`Not a client URL: ${url}`)
    }
    const [have, ...rest] = this.sentData.have
    this.sentData.have = rest
    if (have != null) {
      options.before(undefined, options)
      if (options.setStatus) {
        this.set(STATUS_TYPES.loaded)
      }
      return options.success(this.prepareData(have, options))
    } else {
      options.before(undefined, options)
      const expect = {
        timeout: options.timeout,
        created: Date.now(),
        success: data => options.success(this.prepareData(data, options)),
        error: options.error
      }
      this.sentData.waiting.push(expect)
      if (expect.timeout != null) {
        return setTimeout(() => {
          const idx = this.sentData.waiting.indexOf(expect)
          if (idx !== -1) {
            this.sentData.waiting = this.sentData.waiting.slice(0, idx).concat(this.sentData.waiting.slice(idx + 1))
            if (options.setStatus) {
              this.set(STATUS_TYPES.error)
            }
            return expect.error(new Error(`Timeout ${expect.timeout}ms`))
          }
        }, expect.timeout)
      }
    }
  }

  loadHttp(url, options) {
    return $.ajax({
      url,
      crossDomain: true,
      method: 'GET',
      timeout: options.timeout,
      beforeSend: options.before,
      success: (data, status, xhr) => {
        data = this.prepareData(data, options)
        if (options.setStatus) {
          this.set('loaded')
        }
        return options.success(data, xhr)
      },
      error: (xhr, errorType, error) => {
        options.attempts -= 1
        if (options.attempts > 0) {
          window.debug(`Couldn't load, going to retry up to ${options.attempts} more times`)
          return setTimeout(() => {
            return this.loadHttp(url, options)
          }, 1000)
        } else {
          if (options.setStatus) {
            this.set('error')
          }
          this.submitGiveup()
          return options.error(error, xhr)
        }
      }
    })
  }

  post(msg) {
    if (!isDefined(msg.identity)) {
      msg.identity = this.identity
    }

    if (!this.haveParent()) {
      window.debug('WARNING: No window.parent: postMessage:', msg)
      return
    }

    //const params = new URLSearchParams(window.location.href)
    //const repoType = params.get('repository_type')
    //const isGist = typeof repoType === 'string' && repoType.includes('Gist')
    //const hostname = this.githubHostname//isGist ? `gist.${this.githubHostname}` : this.githubHostname
    const targetOrigin = '*' //this.githubEnv !== 'production' ? '*' : `${location.protocol}//${hostname}`

    window.debug('Render Status:', msg)
    window.parent.postMessage(msg, targetOrigin)
  }

  // Called when a status of "ready" is set for the first time
  onReady() {
    const localTiming = {}
    for (const message of this.messages) {
      localTiming[message.status] = message.when
    }

    return this.submitTiming('local', localTiming)
  }

  onAck() {
    window.debug("Ack'd, sending saved messages")
    this.acked = true
    const result = this.messages.map(msg => {
      if (msg.sent) {
        return
      }
      msg.sent = true
      return this.post({
        type: 'render',
        body: msg.status,
        payload: msg.payload
      })
    })

    return result
  }

  /**
   * Determines whether the render iframe has sent its initial status message to the
   * parent window
   * @param {string} status  The current status of the loader
   * @returns boolean
   */
  requireAck(status = '') {
    return !this.acked && status !== 'hello'
  }

  submitTiming(origin, timing) {
    window.debug(`Got ${origin} timing: ${this.format} => ${JSON.stringify(timing)}`)
    const baseRenderUrl = $('body').data('render-url')

    return $.ajax({
      type: 'POST',
      data: JSON.stringify(timing),
      contentType: 'application/json; charset=utf-8',
      url: `${baseRenderUrl}/stats/timing/${origin}/${this.format}/`,
      success: (data, text, xhr) => {
        return window.debug(`Sent ${origin} timing info:`, data)
      },
      error: (xhr, status, error) => {
        return window.debug('Failed to send remote timing info:', error)
      }
    })
  }

  submitGiveup() {
    const baseRenderUrl = $('body').data('render-url')

    return $.ajax({
      type: 'POST',
      url: `${baseRenderUrl}/stats/${this.format}/gave_up`,
      success: (data, text, xhr) => {
        return window.debug('Sent give up status info.')
      },
      error: (xhr, status, error) => {
        return window.debug('Failed to send give up info:', error)
      }
    })
  }

  handleData(data) {
    if (this.sentData.waiting.length) {
      const [first, ...rest] = this.sentData.waiting
      this.sentData.waiting = rest
      this.set('loaded')
      return first.success(data)
    } else {
      return this.sentData.have.push(data)
    }
  }

  handleCmd(cmd, arg) {
    switch (cmd) {
      case 'branding':
        return $(document.body).removeClass('is-embedded', arg)
      case 'ack':
        if (this.requireAck()) {
          return this.onAck()
        }
        break
      // this has to match the string in github/github/app/assets/modules/github/behaviors/render-editor.ts
      case 'code_rendering_service:data:ready':
        $(document).trigger('code_rendering_service:data:ready', arg.data)
        break
      default:
        return window.debug(`Invalid command '${cmd}':`, arg)
    }
  }

  handleMessage(event) {
    const wantGitHubOrigin = new RegExp(`.${this.githubHostname}$`)
    const {data, origin} = event.originalEvent
    if (!data || !origin) {
      return
    }

    if (!wantGitHubOrigin.test(origin)) {
      return
    }

    const {type, identity, body} = (() => {
      try {
        return JSON.parse(data)
      } catch (error) {
        return data
      }
    })()
    if (!type || !body) {
      return
    }
    if (identity != null && identity !== this.identity) {
      return window.debug(`Message has identity '${identity}', expected '${this.identity}'.`)
    }

    switch (type) {
      case 'render:timing': {
        const {timing, format} = body
        if (!timing || !format) {
          return window.debug('Malformed timing message:', body)
        }
        if (format !== this.format) {
          return window.debug(`Format mismatch: got '${format}' expected '${this.format}'`)
        }
        return this.submitTiming('remote', timing)
      }
      case 'render:cmd': {
        const {cmd} = body
        const arg = body[cmd]
        if (isDefined(cmd) && isDefined(arg)) {
          return this.handleCmd(cmd, arg)
        }
        break
      }
      case 'render:data': {
        return this.handleData(body)
      }
      default:
        return window.debug(`Unknown message type: ${type}`)
    }
  }

  // Is the viewscreen renderer running inside an iframe?
  haveParent() {
    return window.parent !== window
  }

  clientTimeoutAttempts() {
    return $('body').data('client-timeout-attempts')
  }
}

ns.Status = Status

export {InvalidFormatError}
export default Status
